// Isolated PostHog (EU) product-analytics instance for INDEPENDENT.
// - Uses the npm SDK as a NAMED instance ("independent") so it never touches the
//   hosting platform's own window.posthog snippet.
// - Nothing is captured or stored until the user grants consent (opt-in).
// - Renders/works normally when REACT_APP_POSTHOG_KEY is absent (analytics disabled).
import posthog from "posthog-js";
import { captureAttribution } from "./attribution";

const TOKEN = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

export const CONSENT_KEY = "ind_analytics_consent"; // "granted" | "denied"

export const analytics = TOKEN
  ? posthog.init(
      TOKEN,
      {
        api_host: HOST,
        // Manual events only — no autocapture / pageview duplication.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        // Nothing captured or persisted before explicit consent.
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: true,
        persistence: "localStorage+cookie",
        // Session Replay stays OFF until consent; conservative privacy when on.
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
          captureNetworkLogs: false,
        },
      },
      "independent",
    )
  : null;

export const hasAnalytics = Boolean(analytics);

// Acquisition attribution for this tab (sessionStorage, survives OAuth round-trip).
const initialAttribution = (() => {
  const a = captureAttribution() || {};
  const { referrer, ...rest } = a;
  return Object.fromEntries(Object.entries({ ...rest, initial_referrer: referrer }).filter(([, v]) => v));
})();

// Events fired before the user decides on consent are held in memory (never sent
// or stored) and flushed only if consent is granted; dropped on reject.
const pending = [];

export function getConsent() {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(CONSENT_KEY) : null;
  } catch {
    return null;
  }
}

function persistConsent(value) {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {}
}

export function grantAnalyticsConsent() {
  persistConsent("granted");
  if (!analytics) return;
  analytics.opt_in_capturing();
  // Attach initial attribution to all events (set-once = never overwritten later).
  if (Object.keys(initialAttribution).length) analytics.register_once(initialAttribution);
  // Non-sensitive affiliate attribution (FirstPromoter token) if present.
  const ref = readAffiliateRef();
  if (ref) analytics.register({ affiliate_ref: ref });
  analytics.startSessionRecording();
  capturePageview(); // first $pageview after consent
  pending.splice(0).forEach(([e, p, ts]) => analytics.capture(e, p, { timestamp: ts }));
}

// Reads the FirstPromoter referral token cookie (non-sensitive), no personal data.
function readAffiliateRef() {
  try {
    return (document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith("_fprom_ref=")) || "")
      .split("=")[1] || null;
  } catch {
    return null;
  }
}

let _lastPageviewUrl = null;
export function capturePageview() {
  if (!analytics || getConsent() !== "granted") return;
  const url = window.location.href;
  if (url === _lastPageviewUrl) return; // dedup consecutive identical URLs (StrictMode / grant+mount)
  _lastPageviewUrl = url;
  analytics.capture("$pageview", { $current_url: url });
}

export function revokeAnalyticsConsent() {
  persistConsent("denied");
  pending.length = 0;
  if (!analytics) return;
  analytics.stopSessionRecording();
  analytics.opt_out_capturing();
}

export function identifyUser(internalUserId) {
  if (!analytics || !internalUserId) return;
  analytics.identify(String(internalUserId));
  if (Object.keys(initialAttribution).length) analytics.setPersonProperties({}, initialAttribution);
}

export function logoutAnalytics() {
  if (!analytics) return;
  analytics.reset();
}

// Non-sensitive person properties (signup_method, workspace_id, plan/tier).
export function setUserProps(props = {}) {
  if (!analytics) return;
  const clean = Object.fromEntries(
    Object.entries(props).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  if (Object.keys(clean).length) analytics.setPersonProperties(clean);
}

export function capture(event, properties = {}, options = undefined) {
  if (!analytics) return;
  const clean = Object.fromEntries(
    Object.entries(properties).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  const consent = getConsent();
  if (consent === "granted") analytics.capture(event, clean, options);
  else if (consent === null && pending.length < 50) pending.push([event, clean, new Date()]);
}

const ONCE_KEY = "ind_ev_once";
// Fires an event at most once per browser for a given key (e.g. per session/workspace id).
export function captureOnce(event, key, properties = {}) {
  const id = `${event}:${key}`;
  let seen = [];
  try { seen = JSON.parse(window.localStorage.getItem(ONCE_KEY) || "[]"); } catch {}
  if (seen.includes(id)) return false;
  try { window.localStorage.setItem(ONCE_KEY, JSON.stringify([...seen, id].slice(-200))); } catch {}
  capture(event, properties);
  return true;
}

// Stable event names (funnel). Existing events kept (not renamed/removed).
export const EVENTS = {
  landingViewed: "landing_viewed",
  registrationCompleted: "registration_completed", // legacy, kept
  signupStarted: "signup_started",
  signupCompleted: "signup_completed",
  loginCompleted: "login_completed",
  auditStarted: "audit_started",
  auditCompleted: "audit_completed",
  planGenerated: "plan_generated",
  projectCreated: "project_created",
  architectStarted: "architect_started",
  architectCompleted: "architect_completed",
  architectUsed: "architect_used", // legacy (ArchitectHome chat), kept
  buildStarted: "build_started", // legacy, kept
  buildCompleted: "build_completed", // legacy, kept
  checkoutStarted: "checkout_started",
  signupClicked: "signup_clicked",
  workspaceCreated: "workspace_created",
  auditResumed: "audit_resumed",
  signupFailed: "signup_failed",
  loginFailed: "login_failed",
  googleAuthClicked: "google_auth_clicked",
  googleAuthFailed: "google_auth_failed",
  onboardingFailed: "onboarding_failed",
  onboardingAbandoned: "onboarding_abandoned",
  auditStartFailed: "audit_start_failed",
  auditFinalizeFailed: "audit_finalize_failed",
  auditAbandoned: "audit_abandoned",
  checkoutFailed: "checkout_failed",
  checkoutCancelled: "checkout_cancelled",
  inAppBrowserDetected: "inapp_browser_detected",
  inAppOpenExternal: "inapp_open_external",
  inAppCopyLink: "inapp_copy_link",
  // payment_confirmed is fired server-side from the Stripe webhook (consent-gated).
  subscriptionStarted: "subscription_started", // fired server-side via webhook
  subscriptionActivated: "subscription_activated", // legacy frontend event, kept
};
